<template>
	<div class="cat-profile">
		<ProfilePhoto :photo="photo" :keyPrefix="settings.name" v-once />
		<transition
			type="animation"
			name="bounce"
			@after-enter="bouncedIn = true"
			@after-leave="bouncedIn = false"
		>
			<div class="profile-info" v-show="beingInspected"
				:style="{ backgroundColor: settings.profile_background }"
				:class="{ open: bouncedIn }"
			>
				<div class="info-group lg" v-once>
					<h3 class="header">{{ ui.cat_bio_heading }}</h3>
					<ul class="description">
						<li v-for="(item, index) in settings.info" :key="index">
							{{ item.bullet }}
						</li>
					</ul>
				</div>
				<div class="meta-info info-group" v-once>
					<div class="info-group">
						<h3 class="header">{{ ui.cat_scoops_heading }}</h3>
						<div class="info-meta">
							<div class="icon-wrapper">
								<img class="info-icon scoops" src="@assets/img/food-icon.svg" />
							</div>
							<span class="info-value">{{ settings.food_required }}</span>
						</div>
					</div>
					<div class="info-group">
						<h3 class="header">{{ ui.cat_rate_heading }}</h3>
						<div class="info-meta">
							<div class="icon-wrapper">
								<img class="info-icon" src="@assets/img/multiply-icon.svg" />
							</div>
							<span class="info-value">{{ settings.spawn_increase_percentage }}%
								<span class="meta-aside">{{ ui.cat_rate_aside }}</span>
							</span>
						</div>
					</div>
				</div>
				<Button class="select-button" :action="trySelect" v-once>{{ buttonText }}</Button>
			</div>
		</transition>
	</div>
</template>
<script>
import Cookies from 'js-cookie';
import Button from '@/components/abstract/Button';
import ProfilePhoto from '@/components/cat/ProfilePhoto';
import { mapState, mapGetters, mapActions } from 'vuex';
import { whichAnimationEnd } from '@/mixins/event-detection';
import { MIXED_LITTER_COOKIE_KEY } from "@/constants";

const animationEnd = whichAnimationEnd();

export default {
	name: 'CatProfile',
	components: {
		Button,
		ProfilePhoto,
	},
	props: ["settings", "beingInspected"],
	data() {
		return {
			bouncedIn: false,
		};
	},
	computed: {
		...mapGetters(["isLoaded"]),
		...mapState({
			ui: state => state.config.ui.cat_selector,
		}),
		photo() {
			return this.settings.photo.url;
		},
		isMixedLitter() {
			return this.settings.key === "mixed_litter";
		},
		buttonText() {
			return this.isMixedLitter && !this.unlockedMixedLitter ? "Locked" : this.ui.select_button_text;
		},
		unlockedMixedLitter() {
			return Cookies.get(MIXED_LITTER_COOKIE_KEY);
		},
	},
	methods: {
		...mapActions(["sendEvent"]),
		trySelect() {
			if (!this.isMixedLitter || (this.isMixedLitter && this.unlockedMixedLitter)) {
				this.select();
			} else {
				this.deny();
			}
		},
		select() {
			this.sendEvent({
				ec: "Pre-Game",
				ea: "New Game",
				el: this.settings.name,
			});
			this.$store.dispatch("selectCats", this.settings);

			if (!this.isLoaded()) {
				this.$store.commit("setCompletionAction", {
					action: "loadTutorial",
				});
				this.$store.dispatch("loadAssetLoader");
			} else {
				this.$store.dispatch("loadTutorial");
			}
		},
		deny() {
			this.$el.classList.add("shake");
			this.$el.addEventListener(animationEnd, () => {
				this.$el.classList.remove("shake");
			});
		},
	},

};
</script>

<style lang="scss" scoped>
	.cat-profile {
		display: block;
		position: relative;
		color: rgba(255, 255, 255, .7);

		&.shake {
			animation: shake 1s;
		}
	}

	.profile-info {
		width: 100%;
		display: flex !important;
		max-width: 900px;
		padding: 7.5vh 2.5vw;

		justify-content: space-between;
		flex-wrap: wrap;

		position: relative;
		margin: -5% auto 0;

		border-radius: 20px;
		background-color: #42246D;

		opacity: 0;
		pointer-events: none;

		&.open {
			opacity: 1;
			pointer-events: auto;
		}

		@include mobile {
			padding: 5vh 2.5vw 7.5vh;
			margin: -10% auto 0;
		}
	}

	.header {
		padding-bottom: .25em;
		margin-bottom: .25em;

		font-family: 'Asap Condensed';
		text-transform: uppercase;
		white-space: nowrap;
		border-bottom: 2px solid rgba(255, 255, 255, .7);

		@include tablet {
			font-size: 16px;
		}
	}

	.description {
		padding-left: 1.5em;
		font-weight: 400;
		letter-spacing: .03em;
		line-height: 1.4;
		font-size: 22px;

		@include mobile-lg {
			font-size: 16px;
		}
	}

	.info-group {
		flex-basis: 50%;
		width: 50%;
		max-width: 50%; // IE fix
		flex-grow: 0;
		flex-shrink: 0;
		padding: 0 2.5%;

		@include mobile {
			flex-basis: 100%;
			width: 100%;
			max-width: 100%; // IE fix
			padding: 0;

			& {
				width: 50%;
			}
		}
	}

	.info-value {
		font-size: 26px;
		font-weight: 700;
		letter-spacing: .025em;
		color: $brand-color-alt-dark;

		@include mobile {
			font-size: 22px;
		}
	}

	.meta-info {
		display: flex;
		flex-wrap: nowrap;
		justify-content: space-between;

		@include mobile {
			.info-group {
				flex-basis: 45%;
			}
		}
	}

	.info-meta {
		display: flex;
		align-items: center;
		margin-top: 1em;
	}

	// This div is only needed because IE sucks at flexbox, and the scoop icon
	// isn't _actually_ a circle, but we want the circular part of that icon to perfectly
	// match the multiply icon
	.icon-wrapper {
		width: 100px;
		@include mobile-lg {
			width: 75px;
		}
	}

	.info-icon {
		height: 75px;
		@include mobile-lg {
			height: 50px;
		}
	}

	.meta-aside {
		font-size: 12px;
		display: block;
		line-height: 1;
		font-weight: 400;
		margin: -.25em 0 0;
		text-transform: uppercase;
		white-space: nowrap;
	}

	.select-button {
		display: block;
		transform: translate(-50%, -50%);
		margin: 0 auto;
		position: absolute;
		top: 100%;
		left: 50%;
	}
</style>



// WEBPACK FOOTER //
// src/components/cat/CatProfile.vue